import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import EcoIcon from "../../../components/eco-icon/EcoIcon";
import EcoPageHeader from "../../../components/eco-page-header/EcoPageHeader";
import type { RootState } from "../../../store/store";
import { isDpFromToken } from "../../../utils/authToken";

import {
  DpLeaveListItem,
  DpLeaveStatus,
  DpLeaveType,
  LEAVE_STATUS_BADGE,
  LEAVE_STATUS_LABELS,
  LEAVE_TYPE_LABELS,
  listLeavesApi,
} from "../api/leavesApi";
import LeavePayerBadge from "../components/LeavePayerBadge";
import LeaveFormModal from "../components/LeaveFormModal";
import EmployeePickerModal from "../components/EmployeePickerModal";
import type { DpEmployeeListItem } from "../api/employeesApi";
import { formatDateOnly } from "../utils/format";
import { DP_LEAVE_PAGE, DP_PAGE, DP_SENSITIVE_LOGS_PAGE, DP_VACATIONS_PAGE } from "../routes";
import { useGoBack } from "../../../hooks/useGoBack";
import "./Dp.css";
import "./DpEco.css";

const LIMIT = 20;

/**
 * Afastamentos de todos os colaboradores. Sem dado de saúde — o CID e o atestado
 * só aparecem no detalhe, para o papel DP, e cada acesso fica registrado.
 * "Novo afastamento" escolhe o colaborador e abre o mesmo formulário da ficha
 * (aba Afastamentos), que continua sendo o outro caminho de lançamento.
 */
export default function LeavesPage() {
  const navigate = useNavigate();
  const goBack = useGoBack(DP_PAGE());
  const token = useSelector((state: RootState) => state.auth.accessToken);

  const [search, setSearch] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [type, setType] = useState<DpLeaveType | "">("");
  const [status, setStatus] = useState<DpLeaveStatus | "">("ATIVO");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [page, setPage] = useState(1);

  const [items, setItems] = useState<DpLeaveListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  // Lançamento a partir desta tela: primeiro o colaborador, depois o formulário.
  const [picking, setPicking] = useState(false);
  const [creatingFor, setCreatingFor] = useState<{ employee: DpEmployeeListItem; previous: DpLeaveListItem[] } | null>(
    null
  );

  const pickEmployee = async (employee: DpEmployeeListItem) => {
    if (!token) return;
    try {
      // Os afastamentos anteriores alimentam o seletor de "continuação" do formulário.
      const previous = await listLeavesApi(token, { employeeId: employee.id, limit: 100 });
      setPicking(false);
      setCreatingFor({ employee, previous: previous.items });
    } catch (error: any) {
      toast.error(error?.message || "Erro ao carregar os afastamentos do colaborador.");
    }
  };

  // A busca por nome espera o usuário parar de digitar.
  useEffect(() => {
    const timer = setTimeout(() => {
      setPage(1);
      setAppliedSearch(search.trim());
    }, 400);
    return () => clearTimeout(timer);
  }, [search]);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const result = await listLeavesApi(token, {
        page,
        limit: LIMIT,
        search: appliedSearch || undefined,
        type: type || undefined,
        status: status || undefined,
        from: from || undefined,
        to: to || undefined,
      });
      setItems(result.items);
      setTotal(result.total);
    } catch (error: any) {
      toast.error(error?.message || "Erro ao carregar os afastamentos.");
    } finally {
      setLoading(false);
    }
  }, [token, page, appliedSearch, type, status, from, to]);

  useEffect(() => {
    load();
  }, [load]);

  const totalPages = Math.max(1, Math.ceil(total / LIMIT));

  if (!token) return null;

  return (
    <div className="dpLayout">
      <main className="dpMain">
        <div className="dpContainer">
          <EcoPageHeader
            crumbs={[{ label: "Departamento Pessoal", onClick: () => navigate(DP_PAGE()) }, { label: "Afastamentos" }]}
            title="Afastamentos"
            subtitle="Atestados, licenças e afastamentos pelo INSS. Também dá para lançar pela ficha do colaborador."
            onBack={goBack}
            backLabel="Voltar para o DP"
            actions={
              <>
                <button type="button" className="ecoBtn" onClick={() => navigate(DP_VACATIONS_PAGE())}>
                  <EcoIcon name="calendar" size={18} />
                  Férias
                </button>
                {isDpFromToken(token) ? (
                  <button type="button" className="ecoBtn" onClick={() => navigate(DP_SENSITIVE_LOGS_PAGE())}>
                    <EcoIcon name="lock" size={18} />
                    Acessos a dados de saúde
                  </button>
                ) : null}
                <button type="button" className="ecoBtn ecoBtn--primary" onClick={() => setPicking(true)}>
                  <EcoIcon name="plus" size={18} />
                  Novo afastamento
                </button>
              </>
            }
          />

          <div className="dpControls">
            <input
              className="dpInput"
              placeholder="Buscar por nome ou CPF"
              aria-label="Buscar colaborador"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <select
              className="dpSelect"
              value={type}
              aria-label="Filtrar por tipo"
              onChange={(e) => {
                setPage(1);
                setType(e.target.value as DpLeaveType | "");
              }}
            >
              <option value="">Todos os tipos</option>
              {(Object.keys(LEAVE_TYPE_LABELS) as DpLeaveType[]).map((t) => (
                <option key={t} value={t}>
                  {LEAVE_TYPE_LABELS[t]}
                </option>
              ))}
            </select>
            <select
              className="dpSelect"
              value={status}
              aria-label="Filtrar por situação"
              onChange={(e) => {
                setPage(1);
                setStatus(e.target.value as DpLeaveStatus | "");
              }}
            >
              <option value="">Todas as situações</option>
              {(Object.keys(LEAVE_STATUS_LABELS) as DpLeaveStatus[]).map((s) => (
                <option key={s} value={s}>
                  {LEAVE_STATUS_LABELS[s]}
                </option>
              ))}
            </select>
            <input
              type="date"
              className="dpInput"
              aria-label="De"
              title="Afastamentos que tocam o período — de"
              value={from}
              onChange={(e) => {
                setPage(1);
                setFrom(e.target.value);
              }}
            />
            <input
              type="date"
              className="dpInput"
              aria-label="Até"
              title="Afastamentos que tocam o período — até"
              value={to}
              onChange={(e) => {
                setPage(1);
                setTo(e.target.value);
              }}
            />
          </div>

          <div className="dpTableWrapper">
            <table className="dpTable">
              <thead>
                <tr>
                  <th>Colaborador</th>
                  <th>Tipo</th>
                  <th>Período</th>
                  <th>Dias</th>
                  <th>Quem paga</th>
                  <th>Situação</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={6} className="dpEmpty">
                      Carregando...
                    </td>
                  </tr>
                ) : items.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="dpEmpty">
                      Nenhum afastamento encontrado.
                    </td>
                  </tr>
                ) : (
                  items.map((l) => (
                    <tr
                      key={l.id}
                      style={{ cursor: "pointer", ...(l.status === "CANCELADO" ? { opacity: 0.6 } : {}) }}
                      onClick={() => navigate(DP_LEAVE_PAGE(l.id))}
                    >
                      <td>
                        <strong>{l.employee.socialName || l.employee.fullName}</strong>
                      </td>
                      <td>{LEAVE_TYPE_LABELS[l.type]}</td>
                      <td>
                        {formatDateOnly(l.startDate)} a {l.eventEndDate ? formatDateOnly(l.eventEndDate) : "em aberto"}
                      </td>
                      <td>{l.eventDays ?? "—"}</td>
                      <td>
                        <div className="dpTable__actions">
                          <LeavePayerBadge payer={l.payer} />
                          {l.continuations.length > 0 && l.payer !== "INSS" ? <LeavePayerBadge payer="INSS" /> : null}
                        </div>
                      </td>
                      <td>
                        <span className={`dpBadge ${LEAVE_STATUS_BADGE[l.status]}`}>{LEAVE_STATUS_LABELS[l.status]}</span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div className="dpPagination">
            <span>
              Mostrando {items.length} de {total} afastamentos
            </span>
            <div className="dpPagination__btns">
              <button
                className="dpBtn dpBtn--secondary"
                style={{ height: 32 }}
                disabled={page <= 1 || loading}
                onClick={() => setPage((p) => p - 1)}
              >
                Anterior
              </button>
              <span style={{ padding: "0 12px" }}>
                Página {page} de {totalPages}
              </span>
              <button
                className="dpBtn dpBtn--secondary"
                style={{ height: 32 }}
                disabled={page >= totalPages || loading}
                onClick={() => setPage((p) => p + 1)}
              >
                Próxima
              </button>
            </div>
          </div>
        </div>
      </main>

      {picking ? (
        <EmployeePickerModal
          token={token}
          title="Novo afastamento — escolha o colaborador"
          onClose={() => setPicking(false)}
          onPick={pickEmployee}
        />
      ) : null}

      {creatingFor ? (
        <LeaveFormModal
          token={token}
          employeeId={creatingFor.employee.id}
          isDp={isDpFromToken(token)}
          previousLeaves={creatingFor.previous}
          onClose={() => setCreatingFor(null)}
          onSaved={(leaveId) => {
            setCreatingFor(null);
            navigate(DP_LEAVE_PAGE(leaveId));
          }}
        />
      ) : null}
    </div>
  );
}
